"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api, errorMessage, mutation } from "@/lib/api";
import { overridesToCssVars, TOKENS, type ThemeOverrides } from "@/lib/theme/tokens";
import type { Appearance, Theme } from "@/lib/types";
import { useSession } from "@/components/session-provider";

interface ThemeContextValue {
  overrides: ThemeOverrides;
  resolved: ThemeOverrides;
  themes: Theme[];
  activeThemeId: string | null;
  loading: boolean;
  saving: boolean;
  error: string;
  dirty: boolean;
  setOverride: (key: string, value: string) => void;
  resetOverride: (key: string) => void;
  reset: () => void;
  selectTheme: (id: string | null) => void;
  save: () => Promise<void>;
  saveNamed: (name: string) => Promise<void>;
}
const ThemeContext = createContext<ThemeContextValue | null>(null);
const empty: Appearance = {
  activeThemeId: null,
  overrides: {},
  themeTokens: {},
};
function equalOverrides(a: ThemeOverrides, b: ThemeOverrides) {
  const keys = Object.keys(a);
  return (
    keys.length === Object.keys(b).length &&
    keys.every((key) => a[key] === b[key])
  );
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const accountId = useSession().user!.id;
  const [appearance, setAppearance] = useState<Appearance>(empty);
  const [saved, setSaved] = useState<Appearance>(empty);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController();
    void Promise.all([
      api<{ appearance: Appearance }>("/me/appearance", {
        signal: controller.signal,
        headers: { "X-Velnqo-Account": accountId },
      }),
      api<{ themes: Theme[] }>("/themes", {
        signal: controller.signal,
        headers: { "X-Velnqo-Account": accountId },
      }),
    ])
      .then(([appearance, themes]) => {
        if (!controller.signal.aborted) {
          setAppearance(appearance.appearance);
          setSaved(appearance.appearance);
          setThemes(themes.themes || []);
        }
      })
      .catch((error) => {
        if (!controller.signal.aborted) setError(errorMessage(error));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [accountId]);
  const resolved = useMemo(
    () => ({ ...appearance.themeTokens, ...appearance.overrides }),
    [appearance],
  );
  useEffect(() => {
    const root = document.documentElement;
    const variables = overridesToCssVars(resolved);
    const layoutTokens = TOKENS.filter((token) => token.type === "choice");
    for (const token of layoutTokens) root.setAttribute(`data-${token.key.toLowerCase()}`, resolved[token.key] || token.default);
    for (const [key, value] of Object.entries(variables))
      root.style.setProperty(key, value);
    return () => {
      for (const key of Object.keys(variables)) root.style.removeProperty(key);
      for (const token of layoutTokens) root.removeAttribute(`data-${token.key.toLowerCase()}`);
    };
  }, [resolved]);
  const setOverride = useCallback(
    (key: string, value: string) =>
      setAppearance((old) => ({
        ...old,
        overrides: { ...old.overrides, [key]: value },
      })),
    [],
  );
  const resetOverride = useCallback(
    (key: string) =>
      setAppearance((old) => {
        const overrides = { ...old.overrides };
        delete overrides[key];
        return { ...old, overrides };
      }),
    [],
  );
  const reset = useCallback(() => setAppearance(empty), []);
  const selectTheme = useCallback(
    (id: string | null) =>
      setAppearance({
        activeThemeId: id,
        overrides: {},
        themeTokens: themes.find((theme) => theme.id === id)?.tokens || {},
      }),
    [themes],
  );
  const save = useCallback(async () => {
    setSaving(true);
    setError("");
    try {
      const response = await mutation<{ appearance: Appearance }>(
        "/me/appearance",
        {
          activeThemeId: appearance.activeThemeId,
          overrides: appearance.overrides,
        },
        "PUT",
        { headers: { "X-Velnqo-Account": accountId } },
      );
      setSaved(response.appearance);
    } catch (error) {
      setError(errorMessage(error));
      throw error;
    } finally {
      setSaving(false);
    }
  }, [appearance, accountId]);
  const saveNamed = useCallback(
    async (name: string) => {
      setSaving(true);
      setError("");
      try {
        const { theme } = await mutation<{ theme: Theme }>(
          "/themes",
          {
            name,
            tokens: resolved,
            isPublic: false,
          },
          "POST",
          { headers: { "X-Velnqo-Account": accountId } },
        );
        setThemes((old) => [...old, theme]);
      } catch (error) {
        setError(errorMessage(error));
        throw error;
      } finally {
        setSaving(false);
      }
    },
    [resolved, accountId],
  );
  const dirty =
    saved.activeThemeId !== appearance.activeThemeId ||
    !equalOverrides(saved.overrides, appearance.overrides);
  return (
    <ThemeContext.Provider
      value={{
        overrides: appearance.overrides,
        resolved,
        themes,
        activeThemeId: appearance.activeThemeId,
        loading,
        saving,
        error,
        dirty,
        setOverride,
        resetOverride,
        reset,
        selectTheme,
        save,
        saveNamed,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}
export function useTheme() {
  const theme = useContext(ThemeContext);
  if (!theme) throw new Error("ThemeProvider is required");
  return theme;
}
